import storage, { AUTH_TOKEN_KEY } from '../../lib/storage';
import { Transaction } from './models/transaction';
import { getBackendUrl } from '../../lib/backendUrl';
import { fetchWithRetry } from '../../lib/fetchWithRetry';

// Runtime config helper — prefers injected `window.__RUNTIME_CONFIG__` on web
function getRuntimeConfig() {
  if (typeof window !== 'undefined' && (window as any).__RUNTIME_CONFIG__) return (window as any).__RUNTIME_CONFIG__ as Record<string,string>;
  return undefined as unknown as Record<string,string> | undefined;
}

const runtime = getRuntimeConfig();
// Default backend port is 4000 (Express). Runtime config / env can override.
const BACKEND_URL = getBackendUrl(runtime && runtime.BACKEND_URL);

async function authHeaders() {
  const token = await storage.getSecureItem ? await storage.getSecureItem(AUTH_TOKEN_KEY) : await storage.loadAuthToken();
  const deviceIdKey = 'kakeibo_device_id';
  let deviceId = await storage.getItem(deviceIdKey);
  if (!deviceId) {
    deviceId = `inst-${Date.now()}-${Math.random().toString(36).slice(2,9)}`;
    try { await storage.setItem(deviceIdKey, deviceId); } catch (_) {}
  }

  const platform = (typeof navigator !== 'undefined' && (navigator.product || 'web')) as string;
  const baseHeaders: Record<string,string> = {
    'Content-Type': 'application/json',
    'X-Device-Id': deviceId,
    'X-App-Id': 'kakeibo',
    'X-Platform': platform,
  };
  if (token) baseHeaders.Authorization = `Bearer ${token}`;
  return baseHeaders;
}

const STORAGE_KEY = 'kakeibo_transactions_v1';

function uuidv4() {
  // RFC4122-ish v4 generator (good enough for IDs; not crypto-grade)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function newTransactionId() {
  return uuidv4();
}

export async function fetchLocalTransactions(): Promise<Transaction[]> {
  const raw = await storage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    return raw as Transaction[];
  } catch {
    return [];
  }
}

export async function saveLocalTransactions(txns: Transaction[]) {
  await storage.setItem(STORAGE_KEY, txns);
}

export async function fetchRemoteTransactions(userId?: string) {
  const h = await authHeaders();
  const res = await fetchWithRetry(`${BACKEND_URL.replace(/\/+$/, '')}/transactions`, { headers: h, credentials: 'include' }, { retries: 3 });
  if (!res.ok) throw new Error('Failed to fetch transactions');
  const data = await res.json();
  return data as Transaction[];
}

export async function createTransaction(tx: Transaction) {
  // optimistic local save
  const local = await fetchLocalTransactions();
  local.unshift(tx);
  await saveLocalTransactions(local);

  // push to backend in background
  try {
    const h = await authHeaders();
    const res = await fetchWithRetry(`${BACKEND_URL.replace(/\/+$/, '')}/transactions`, {
      method: 'POST',
      headers: h,
      credentials: 'include',
      body: JSON.stringify(tx),
    }, { retries: 2 });
    if (!res.ok) {
      console.warn('Backend insert failed', await res.text());
    }
  } catch (err: any) {
    console.warn('Background transaction push failed, enqueueing for retry', err?.message || err);
    // persist request for later retry
    try {
      const { enqueueRequest } = await import('../../lib/requestQueue');
      await enqueueRequest({ url: `${BACKEND_URL.replace(/\/+$/, '')}/transactions`, init: { method: 'POST', headers: await authHeaders(), credentials: 'include', body: JSON.stringify(tx) } });
    } catch (e) {
      console.warn('Failed to enqueue request', e);
    }
  }
}

export async function updateTransaction(id: string, updates: Partial<Transaction>) {
  // optimistic local update
  const local = await fetchLocalTransactions();
  const next = local.map((t) => (t.id === id ? { ...t, ...updates } : t));
  await saveLocalTransactions(next);

  try {
    const h = await authHeaders();
    const res = await fetchWithRetry(`${BACKEND_URL.replace(/\/+$/, '')}/transactions/${id}`, {
      method: 'PUT',
      headers: h,
      credentials: 'include',
      body: JSON.stringify(updates),
    }, { retries: 2 });
    if (!res.ok) {
      console.warn('Backend update failed', await res.text());
      return null;
    }
    const data = await res.json();
    // reconcile local with server version
    const reconciled = (await fetchLocalTransactions()).map((t) => (t.id === id ? { ...t, ...data } : t));
    await saveLocalTransactions(reconciled);
    return data as Transaction;
  } catch (err: any) {
    console.warn('Background transaction update failed', err?.message || err);
    return null;
  }
}

export async function deleteTransaction(id: string): Promise<boolean> {
  const local = (await fetchLocalTransactions()).filter(t => t.id !== id);
  await saveLocalTransactions(local);
  try {
    const h = await authHeaders();
    const res = await fetchWithRetry(`${BACKEND_URL.replace(/\/+$/, '')}/transactions/${id}`, { method: 'DELETE', headers: h, credentials: 'include' }, { retries: 2 });
    return !!res.ok;
  } catch (err) {
    // ignore background delete failures for now
    return false;
  }
}

// Ensure default categories exist for a given user (creates user-specific categories)
export async function ensureDefaultCategories(userId: string) {
  if (!userId) return;
  const defaults = [
    // Small optional starter set (user-owned)
    { name: 'Coffee', type: 'expense', color: '#6c5ce7' },
    { name: 'Groceries', type: 'expense', color: '#00b894' },
    { name: 'Salary', type: 'income', color: '#2ecc71' }
  ];

  try {
    // Fetch existing categories from backend
    const h = await authHeaders();
    const res = await fetchWithRetry(`${BACKEND_URL.replace(/\/+$/, '')}/categories`, { headers: h, credentials: 'include' }, { retries: 3 });
    if (!res.ok) throw new Error('Failed to fetch categories');
    const existing = await res.json();
    const existingNames = new Set((existing || []).map((r: any) => r.name));
    for (const d of defaults) {
      if (!existingNames.has(d.name)) {
        await fetchWithRetry(`${BACKEND_URL.replace(/\/+$/, '')}/categories`, {
          method: 'POST',
          headers: h,
          credentials: 'include',
          body: JSON.stringify({ ...d }),
        }, { retries: 2 });
      }
    }
  } catch (err: any) {
    console.warn('ensureDefaultCategories error', err?.message || err);
  }
}

// Fetch categories visible to the user: global (user_id IS NULL) + user-specific
export async function fetchCategories(userId?: string) {
  try {
    const h = await authHeaders();
    const res = await fetchWithRetry(`${BACKEND_URL.replace(/\/+$/, '')}/categories`, { headers: h, credentials: 'include' }, { retries: 3 });
    if (!res.ok) throw new Error('Failed to fetch categories');
    return (await res.json()) || [];
  } catch (err) {
    console.warn('fetchCategories error', err);
    return [];
  }
}

export async function createCategory(userId: string, payload: { name: string; type: 'income' | 'expense'; color?: string }) {
  try {
    const h = await authHeaders();
    const res = await fetchWithRetry(`${BACKEND_URL.replace(/\/+$/, '')}/categories`, {
      method: 'POST', headers: h, credentials: 'include', body: JSON.stringify({ ...payload })
    }, { retries: 2 });
    if (!res.ok) throw new Error('Failed to create category');
    const data = await res.json();
    return data || null;
  } catch (err) {
    console.warn('createCategory error', err);
    return null;
  }
}

export async function updateCategory(id: string, updates: { name?: string; color?: string; type?: 'income' | 'expense' }) {
  try {
    const h = await authHeaders();
    const res = await fetchWithRetry(`${BACKEND_URL.replace(/\/+$/, '')}/categories/${id}`, {
      method: 'PUT',
      headers: h,
      credentials: 'include',
      body: JSON.stringify(updates),
    }, { retries: 2 });
    if (!res.ok) throw new Error('Failed to update category');
    return (await res.json()) || null;
  } catch (err) {
    console.warn('updateCategory error', err);
    return null;
  }
}

export async function deleteCategory(id: string) {
  try {
    const h = await authHeaders();
    const res = await fetchWithRetry(`${BACKEND_URL.replace(/\/+$/, '')}/categories/${id}`, {
      method: 'DELETE',
      headers: h,
      credentials: 'include',
    }, { retries: 2 });
    if (!res.ok) throw new Error('Failed to delete category');
    const data = await res.json();
    return !!(data && data.ok);
  } catch (err) {
    console.warn('deleteCategory error', err);
    return false;
  }
}
